import { createFileRoute } from "@tanstack/react-router";
import { Languages, PenLine, Wand2 } from "lucide-react";
import { FeaturePage } from "@/components/marketing/FeaturePage";
import { alternateLinkList } from "@/lib/i18n/locale";
import heroImg from "@/assets/ai-tools-hero.webp";
import contextImg from "@/assets/ai-tools-context.webp";
import configImg from "@/assets/ai-tools-config.webp";

const IMG: Record<string, string> = { hero: heroImg, context: contextImg, config: configImg };

export const Route = createFileRoute("/vi/platform/ai-tools")({
  head: () => ({
    meta: [
      { title: "Công cụ AI — banana.cards" },
      { name: "description", content: "Viết, tóm tắt, dịch và chấm điểm với AI — ngay nơi bạn làm việc trong Banana." },
      { property: "og:title", content: "Công cụ AI — banana.cards" },
      { property: "og:description", content: "Viết, tóm tắt, dịch và chấm điểm với AI." },
      { property: "og:image", content: IMG['hero'] },
      { property: "og:locale", content: "vi_VN" },
    ],
    links: alternateLinkList("/platform/ai-tools", "vi"),
  }),
  component: Page,
});

function Page() {
  return (
    <FeaturePage
      eyebrow="Công cụ AI"
      title="Một bộ AI,"
      italic="trong workflow của bạn."
      description="Viết, tóm tắt, dịch, chấm điểm và gợi ý bước tiếp. Công cụ AI của Banana sống đúng nơi bạn làm việc — inbox, CRM, điện thoại."
      heroImage={IMG['hero']}
      metrics={[{ v: "12+", l: "công cụ AI" }, { v: "50+", l: "ngôn ngữ" }, { v: "1-tap", l: "action" }]}
      bullets={[
        { icon: PenLine, title: "AI writer", desc: "Soạn email, proposal, follow-up và ghi chú — theo giọng của bạn." },
        { icon: Languages, title: "AI translator", desc: "Nói với khách bằng ngôn ngữ của họ — không phải copy sang công cụ khác." },
        { icon: Wand2, title: "Next-best action", desc: "AI gợi ý ai cần liên hệ tiếp và nói gì, dựa trên ngữ cảnh thật." }
      ]}
      deepDives={[
        { eyebrow: "Có ngữ cảnh", title: "Ngay trong", italic: "view liên hệ.", description: "Mọi công cụ AI cách 1 tap từ contact, deal hoặc inbox — không chuyển app, không copy-paste.", image: IMG["context"], reverse: false, points: ["Tóm tắt email và cuộc họp", "Soạn reply có ngữ cảnh", "Tự chấm điểm deal", "Sinh proposal từ contact"] },
        { eyebrow: "Tùy chỉnh", title: "Chọn model,", italic: "kiểm soát chi phí.", description: "Chọn model cho mỗi công cụ từ config trung tâm — Banana theo dõi usage và không hard-code vendor.", image: IMG["config"], reverse: true, points: ["Registry model trung tâm", "Chọn model theo tính năng", "Log usage & giới hạn chi phí", "BYO API key cho enterprise"] }
      ]}
      howItWorks={[
        { title: "Chọn công cụ", desc: "Writer, translator, summarizer, scorer." },
        { title: "1-tap từ ngữ cảnh", desc: "Banana tự kéo dữ liệu." },
        { title: "Review & sửa", desc: "Bạn kiểm soát mọi output." },
        { title: "Theo dõi usage", desc: "Thấy chi phí và tác động mỗi công cụ." }
      ]}
      capabilities={["AI email writer & rewriter", "AI tóm tắt cuộc họp", "AI dịch (50+ ngôn ngữ)", "AI chấm điểm lead", "AI gợi ý next step", "AI transcribe voice note", "AI sinh proposal", "Config model trung tâm"]}
      faq={[
        { q: "Banana có những công cụ AI nào?", a: "Scanner thẻ, Notetaker cuộc họp, Email Drafter, Contact Enrichment, Scout, Contract Drafter, Smart Tagging, dịch tự động, Next-Best-Action." },
        { q: "Có BYOK (bring your own key) không?", a: "Có — gói Enterprise cho phép plug OpenAI/Anthropic/Google AI key của bạn." },
        { q: "Có thấy chi phí mỗi công cụ không?", a: "Có — dashboard AI Usage ghi mỗi invocation với tên công cụ, model, token và chi phí." },
        { q: "Admin có thể giới hạn usage không?", a: "Có — đặt cap theo người dùng, team hoặc tính năng." }
      ]}
      outro={{ title: "AI không cản đường.", italic: "AI có mặt khi cần.", description: "Công cụ xuất hiện đúng chỗ bạn cần — và không ở chỗ khác." }}
    />
  );
}